import { BaseModal } from '@shootsta/common-react';
import HALLOWEEN_OFFER_IMAGE from '../assets/halloween-offer.png';
import { TopupPromo } from '../../TopupCreditsModal/utils/topupPromo';

import '../styles/_halloween-promo-modal.scss';

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten'
];

const inWords = (value: number) => NUMBER_WORDS[value] ?? String(value);

const capitalise = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const credits = (value: number) => `${value} credit${value === 1 ? '' : 's'}`;

type Props = {
  visible: boolean;
  promo: TopupPromo;
  claiming?: boolean;
  error?: string | null;
  onClaim: () => void;
  onClose: () => void;
};

/**
 * SOW 49: the Halloween offer popup the promo banner opens. Explains what the
 * workspace gets, and "Claim my free credit" goes straight to Stripe for
 * exactly `buyQuantity` credits. The quantities come from the
 * `topup-credits-promos` flag, the same values checkout applies.
 */
export default function HalloweenPromoModal({
  visible,
  promo,
  claiming = false,
  error = null,
  onClaim,
  onClose
}: Props) {
  const { buyQuantity, freeQuantity } = promo;
  const total = buyQuantity + freeQuantity;

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      size="SM"
      isAutoWidth={false}
      showHeaderCloseButton={false}
      className="halloween-promo-modal"
    >
      <button
        type="button"
        className="halloween-promo-modal__close"
        aria-label="Close"
        onClick={onClose}
      >
        &times;
      </button>
      <img
        className="halloween-promo-modal__hero"
        src={HALLOWEEN_OFFER_IMAGE}
        alt={`${credits(buyQuantity)} plus ${freeQuantity} free`}
      />
      <div className="halloween-promo-modal__content">
        <p className="halloween-promo-modal__eyebrow">Halloween offer</p>
        <h2 className="halloween-promo-modal__title">
          Buy {credits(buyQuantity)}, get {freeQuantity} free
        </h2>
        <p className="halloween-promo-modal__body">
          Buy {credits(buyQuantity)} in one purchase before 31 October and
          we&apos;ll add {credits(freeQuantity)} to your account at no cost.
          That&apos;s {inWords(total)} videos for the price of{' '}
          {inWords(buyQuantity)}. All treat, no trick.
        </p>
        <p className="halloween-promo-modal__terms">
          {capitalise(inWords(freeQuantity))} free{' '}
          {freeQuantity === 1 ? 'credit' : 'credits'} per eligible purchase.
          Offer ends 11:59pm AEDT, 31 October 2026. Terms apply.
        </p>
        {error && (
          <p className="halloween-promo-modal__error" role="alert">
            {error}
          </p>
        )}
        <div className="halloween-promo-modal__actions">
          <button
            type="button"
            className="halloween-promo-modal__claim"
            onClick={onClaim}
            disabled={claiming}
          >
            {claiming ? 'Opening checkout…' : 'Claim my free credit'}
          </button>
          <button
            type="button"
            className="halloween-promo-modal__dismiss"
            onClick={onClose}
          >
            Not right now
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
